import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { emailRegexPattern } from '../../helper';
import styles from './ListedDelistConfirmModal.module.css';
import manageTicketStyles from './ManageTickets.module.css';
import {
  DELIST_UI_STEPS,
  DelistJobProgress,
  DelistUiStep,
  delistStepState,
} from './delistStepStatus';

type Props = {
  ticketCount: number;
  enableReturnEmailSelection?: boolean;
  qaUserEmail?: string;
  storedReturnEmail?: string;
  loading?: boolean;
  progress?: DelistJobProgress | null;
  onCancel: () => void;
  onConfirm: (requestedEmailId?: string) => void | Promise<void>;
  onRetryStep?: (step: DelistUiStep, requestedEmailId?: string) => void;
};

export function ListedDelistConfirmModal({
  ticketCount,
  enableReturnEmailSelection = false,
  qaUserEmail = '',
  storedReturnEmail = '',
  loading = false,
  progress = null,
  onCancel,
  onConfirm,
  onRetryStep,
}: Props) {
  const authenticatedEmail = qaUserEmail.trim();
  const existingReturnEmail = storedReturnEmail.trim();
  const [emailMode, setEmailMode] = useState<'stored' | 'qa' | 'different'>(
    existingReturnEmail ? 'stored' : authenticatedEmail ? 'qa' : 'different',
  );
  const [emailModeTouched, setEmailModeTouched] = useState(false);
  const [returnEmail, setReturnEmail] = useState('');
  const [confirmReturnEmail, setConfirmReturnEmail] = useState('');
  const [returnEmailError, setReturnEmailError] = useState('');
  const [confirmReturnEmailError, setConfirmReturnEmailError] = useState('');
  const ticketLabel = ticketCount > 1 ? `${ticketCount} tickets` : 'this ticket';
  const failedStep = progress?.failedStep ?? null;
  const started = loading || Boolean(progress?.currentStep || progress?.failedStep || progress?.completedSteps?.length);
  const showSteps = started;
  const canRetry = Boolean(failedStep && onRetryStep && !loading);

  useEffect(() => {
    if (existingReturnEmail) {
      setEmailMode('stored');
    } else if (authenticatedEmail && !emailModeTouched) {
      setEmailMode('qa');
    }
  }, [authenticatedEmail, emailModeTouched, existingReturnEmail]);

  const submitDelist = () => {
    if (!enableReturnEmailSelection) {
      void onConfirm();
      return;
    }
    const email = emailMode === 'stored'
      ? existingReturnEmail
      : emailMode === 'qa'
        ? authenticatedEmail
        : returnEmail.trim();
    const confirmEmail = confirmReturnEmail.trim();
    const isValid = emailMode !== 'different' || emailRegexPattern.test(email);
    setReturnEmailError(
      emailMode === 'stored'
        ? ''
        : emailMode === 'qa'
        ? authenticatedEmail ? '' : 'Select an active QA email'
        : !email ? 'Email is required' : isValid ? '' : 'Enter a valid email',
    );
    setConfirmReturnEmailError(
      emailMode !== 'different'
        ? ''
        : !confirmEmail
          ? 'Confirm Email is required'
          : confirmEmail !== email
            ? 'Emails do not match'
            : '',
    );
    if (!email || !isValid || (emailMode === 'different' && (!confirmEmail || confirmEmail !== email))) {
      return;
    }
    setReturnEmailError('');
    void onConfirm(email);
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.iconWrap}>
              <AlertTriangle color="#b91c1c" size={18} />
            </div>
            <div className={styles.titleBlock}>
              <div className={styles.title}>Unshare and Delist</div>
              <div className={styles.subtitle}>
                {loading
                  ? 'Please wait — this may take a few seconds'
                  : failedStep
                    ? 'A step failed. Retry that step to continue.'
                    : 'Confirm before unsharing, unlisting, and moving tickets to Delisted'}
              </div>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            disabled={loading}
            onClick={onCancel}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {showSteps ? (
          <div className={styles.processing} role="status" aria-live="polite">
            <div className={styles.processingText}>
              <b>{loading ? 'Working on it…' : failedStep ? 'Delist stopped' : 'Delist progress'}</b>
              <ol className={styles.stepList}>
                {DELIST_UI_STEPS.map((step) => {
                  const state = delistStepState(step.id, progress, loading);
                  return (
                    <li key={step.id} className={styles.stepRow} data-state={state}>
                      <span className={styles.stepIcon} aria-hidden="true">
                        {state === 'active' ? (
                          <span className={styles.stepSpinner} />
                        ) : state === 'success' ? (
                          <CheckCircle2 size={18} color="#16a34a" />
                        ) : state === 'failed' ? (
                          <span className={styles.stepFailDot} />
                        ) : (
                          <span className={styles.stepPendingDot} />
                        )}
                      </span>
                      <span className={styles.stepLabel}>{step.label(ticketLabel)}</span>
                      {state === 'failed' && canRetry ? (
                        <button
                          type="button"
                          className={styles.retryBtn}
                          onClick={() => enableReturnEmailSelection
                            ? onRetryStep?.(
                                step.id,
                                emailMode === 'stored'
                                  ? existingReturnEmail
                                  : emailMode === 'qa' ? authenticatedEmail : returnEmail.trim(),
                              )
                            : onRetryStep?.(step.id)}
                        >
                          Retry
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
              {progress?.message && failedStep ? (
                <p className={styles.stepError}>{progress.message}</p>
              ) : (
                <p className={styles.processingHint}>
                  {loading
                    ? 'Do not close this window until it finishes.'
                    : 'Only the failed step will run again. Completed steps stay done.'}
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className={styles.warning}>
              <AlertTriangle color="#b91c1c" size={18} />
              <div>
                <b>Continue with unshare and delist?</b>
                <p>
                  This will unshare the listing, set the Victory Live price to 1¢, append a period to
                  the row (e.g. TEST1 → TEST1.), unlist that inventory, then move {ticketLabel} to
                  Delisted.
                </p>
              </div>
            </div>
            {enableReturnEmailSelection && (
            <div className={`${styles.returnEmailField} ${manageTicketStyles.editField}`}>
              <label>Return Email *</label>
              {existingReturnEmail ? (
                <input
                  type="email"
                  aria-label="Stored Return Email"
                  className={manageTicketStyles.returnEmailSelect}
                  value={existingReturnEmail}
                  readOnly
                />
              ) : (
              <>
              <div className={manageTicketStyles.returnEmailOptions}>
                <label className={manageTicketStyles.returnEmailOption}>
                  <input
                    type="radio"
                    name="listed-delist-email-mode"
                    checked={emailMode === 'qa'}
                    disabled={!authenticatedEmail}
                    onChange={() => {
                      setEmailModeTouched(true);
                      setEmailMode('qa');
                      setReturnEmailError('');
                      setConfirmReturnEmailError('');
                    }}
                  />
                  <span>Use QA User Email</span>
                </label>
                <label className={manageTicketStyles.returnEmailOption}>
                  <input
                    type="radio"
                    name="listed-delist-email-mode"
                    checked={emailMode === 'different'}
                    onChange={() => {
                      setEmailModeTouched(true);
                      setEmailMode('different');
                      setReturnEmail('');
                      setConfirmReturnEmail('');
                      setReturnEmailError('');
                      setConfirmReturnEmailError('');
                    }}
                  />
                  <span>Use Different Email</span>
                </label>
              </div>
              {emailMode === 'qa' && authenticatedEmail ? (
                <input
                  type="email"
                  aria-label="QA User email"
                  className={manageTicketStyles.returnEmailSelect}
                  value={authenticatedEmail}
                  readOnly
                  
                />
              ) : emailMode === 'different' ? (
                <>
                <div className={manageTicketStyles.editField}>
                  <label htmlFor="listed-delist-return-email">Email</label>
                <input
                  type="email"
                  id="listed-delist-return-email"
                  aria-label="Return email address"
                  placeholder="name@company.com"
                  value={returnEmail}
                  onChange={(event) => {
                    setReturnEmail(event.target.value);
                    setReturnEmailError('');
                  }}
                />
                {returnEmailError && <small className={manageTicketStyles.errorText}>{returnEmailError}</small>}
                </div>
                <div className={manageTicketStyles.editField}>
                  <label htmlFor="listed-delist-confirm-return-email">Confirm Email</label>
                  <input
                    type="email"
                    id="listed-delist-confirm-return-email"
                    aria-label="Confirm return email address"
                    placeholder="name@company.com"
                    value={confirmReturnEmail}
                    onChange={(event) => {
                      setConfirmReturnEmail(event.target.value);
                      setConfirmReturnEmailError('');
                    }}
                  />
                  {confirmReturnEmailError && <small className={manageTicketStyles.errorText}>{confirmReturnEmailError}</small>}
                </div>
                </>
              ) : null}
              {!authenticatedEmail && emailMode === 'different' && (
                <small className={manageTicketStyles.returnEmailState}>
                  The current QA user email is unavailable. Enter a different email.
                </small>
              )}
              {emailMode === 'qa' && returnEmailError && <small className={manageTicketStyles.errorText}>{returnEmailError}</small>}
              </>
              )}
            </div>
            )}
          </>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} disabled={loading} onClick={onCancel}>
            {failedStep ? 'Close' : 'Cancel'}
          </button>
          {!showSteps ? (
            <button
              type="button"
              className={styles.confirmBtn}
              disabled={loading}
              onClick={submitDelist}
            >
              Unshare & Delist
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
